import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { CircleAlert } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Ghost } from '@/components/Icons/Ghost';
import { Hourglass } from '@/components/Icons/Hourglass';
import { Button } from '@/components/ui/button';
import { MODAL_TIMEOUT } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { shouldDisplaySurveySelector, chatModalState, isWidgetExpandedState, } from '@/state/session';
import { webChatStateState } from '@/state/socket';
import { sleep } from '@/util/utils';
/**
 * Shows a chat form when the chatmodal is set to `closing`, `inactivity` or `expired`.
 */
export const CloseChat = ({ type }) => {
    const { t } = useTranslation();
    const { closeSession } = useSocket();
    const { closeWidget } = useWidgetState();
    const setWebChatState = useSetRecoilState(webChatStateState);
    const isExpanded = useRecoilValue(isWidgetExpandedState);
    const { trackEvent } = useAnalytics();
    const shouldDisplaySurvey = useRecoilValue(shouldDisplaySurveySelector);
    const setChatModal = useSetRecoilState(chatModalState);
    /**
     * Modal config map with type-specific values
     */
    const modalConfigs = useMemo(() => ({
        ["closing" /* ChatModal.Closing */]: {
            icon: _jsx(CircleAlert, { size: 40, color: "var(--color-foreground-muted)" }),
            title: t('modal.end'),
            primaryButton: t('buttons.end_chat'),
            secondaryButton: t('buttons.cancel'),
            primaryEventTracker: AnalyticsEvent.PopoverEndChatClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverEndChatContinueClicked,
        },
        ["expired" /* ChatModal.Expired */]: {
            icon: _jsx(Hourglass, {}),
            title: t('modal.expired_title'),
            subtitle: t('modal.expired_subtitle'),
            primaryButton: t('buttons.start_new_chat'),
            secondaryButton: t('buttons.close'),
            primaryEventTracker: AnalyticsEvent.PopoverExpiredNewClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverExpiredEndClicked,
        },
        ["inactivity" /* ChatModal.Inactivity */]: {
            icon: _jsx(Ghost, {}),
            title: t('modal.inactivity_title'),
            subtitle: t('modal.inactivity'),
            primaryButton: t('buttons.continue'),
            secondaryButton: t('buttons.start_new_chat'),
            primaryEventTracker: AnalyticsEvent.PopoverInactivityEndChatClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverInactivityContinueClicked,
        },
        // Add fallbacks for other ChatModal types to prevent TypeScript errors
        ["survey" /* ChatModal.Survey */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["survey-full" /* ChatModal.SurveyFull */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["closed" /* ChatModal.Resolved */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["email" /* ChatModal.Email */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
    }), [t]);
    const config = useMemo(() => modalConfigs[type], [modalConfigs, type]);
    /**
     * Primary button click handler
     */
    const handlePrimaryClick = useCallback(async () => {
        if (config.primaryEventTracker) {
            trackEvent(config.primaryEventTracker);
        }
        setChatModal(null);
        if (type === "inactivity" /* ChatModal.Inactivity */) {
            return;
        }
        if (shouldDisplaySurvey) {
            await sleep(MODAL_TIMEOUT);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            // Cleaning last session
            closeSession();
            // If the session was expired, and the user selects to start a new one, we need to
            // reinitiate the conversation
            if (type === "expired" /* ChatModal.Expired */) {
                setWebChatState("init" /* WebChatState.Init */);
            }
        }
    }, [
        closeSession,
        config.primaryEventTracker,
        setChatModal,
        setWebChatState,
        shouldDisplaySurvey,
        trackEvent,
        type,
    ]);
    /**
     * Secondary button click handler
     */
    const handleSecondaryClick = useCallback(async () => {
        if (config.secondaryEventTracker) {
            trackEvent(config.secondaryEventTracker);
        }
        setChatModal(null);
        if (type === "expired" /* ChatModal.Expired */) {
            if (shouldDisplaySurvey) {
                await sleep(MODAL_TIMEOUT);
                setChatModal("survey" /* ChatModal.Survey */);
            }
            else {
                closeSession();
                closeWidget();
            }
        }
        if (type === "inactivity" /* ChatModal.Inactivity */) {
            closeSession();
        }
    }, [
        closeSession,
        closeWidget,
        config.secondaryEventTracker,
        setChatModal,
        shouldDisplaySurvey,
        trackEvent,
        type,
    ]);
    return (_jsxs("div", { className: "flex h-full flex-col items-center justify-center gap-4 p-4", children: [config === null || config === void 0 ? void 0 : config.icon, _jsxs("div", { className: "text-surface-card-foreground mx-auto text-center text-lg font-semibold", children: [config.title, config.subtitle && (_jsx("div", { className: "text-foreground-muted mx-auto mt-1 max-w-xs text-center text-sm leading-5 font-normal", children: config.subtitle }))] }), _jsxs("div", { className: "flex w-full flex-col items-center justify-center gap-2", children: [_jsx(Button, { className: cn(`${"end-btn" /* TestClassName.EndSessionButton */} w-full`, {
                            'w-sm': isExpanded,
                        }), onClick: handlePrimaryClick, children: config.primaryButton }), _jsx(Button, { onClick: handleSecondaryClick, variant: "outline-neutral", className: cn('w-full', {
                            'w-sm': isExpanded,
                        }), children: config.secondaryButton })] })] }));
};
